import React from "react";
import { getListQueryStatus } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	Conditional,
	filterAndSortByQuery,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	toLocalDateString,
	TruncatedText,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { SearchField } from "../../../../Components/Search";
import {
	CourseAssessment,
	CourseAssessmentTimingOrderField,
	CourseSection,
	CourseSectionGradeBookQuery,
	OrderDirection,
	Student,
	StudentNameAndImageFragment,
	useCourseSectionGradeBookQuery,
	useSearchFormState,
	useStudentNamesAndImagesQuery,
} from "../../../../Utilities";
import { CourseAssessmentFormat, CourseAssessmentTimingFormat, StudentFormat } from "../../../Format";
import { StudentIcon } from "../../Student";
import { ControlledCourseAssessmentModal } from "../Modal";
import { StudentCourseSectionGradeBookModal } from "../StudentCourseSectionGradeBook";
import { SubmissionStatusBadge } from "../SubmissionStatusBadge";
import styles from "./styles.module.css";

export type CourseSectionGradeBookModalProps = CourseSectionGradeBookProps &
	Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function CourseSectionGradeBookModal({
	courseSectionID,
	ifRef,
	onClose,
	className,
	style,
}: CourseSectionGradeBookModalProps) {
	return (
		<Modal size="full" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Grade Book</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<CourseSectionGradeBook courseSectionID={courseSectionID} />
			</Modal.Body>
		</Modal>
	);
}

export type CourseSectionGradeBookProps = {
	courseSectionID: CourseSection["id"];
};

export function CourseSectionGradeBook({ courseSectionID }: CourseSectionGradeBookProps) {
	const searchFormState = useSearchFormState();
	const { search } = searchFormState.formValues;

	const { data: studentData } = useStudentNamesAndImagesQuery({
		variables: {
			filters: {
				studentCourseSectionEnrollments: [{ courseSectionIDEQ: courseSectionID }],
			},
		},
	});
	const filteredStudentEdges = React.useMemo(() => {
		if (!studentData) {
			return [];
		}
		return filterAndSortByQuery(search, studentData.studentConnection.edges, (e) => StudentFormat.name(e.node));
	}, [search, studentData]);

	const {
		loading: loadingGradeBook,
		data: gradeBookData,
		error: gradeBookError,
	} = useCourseSectionGradeBookQuery({
		variables: {
			courseSectionID,
			filters: { dueOnLTE: toLocalDateString(new Date()) },
			order: { field: CourseAssessmentTimingOrderField.DueOn, direction: OrderDirection.Desc },
		},
	});
	// TODO pagination
	const gradeBookQueryStatus = getListQueryStatus({
		loading: loadingGradeBook,
		data: gradeBookData,
		error: gradeBookError,
		resultKey: "courseAssessmentTimingConnection",
	});

	const { columns, columnWidths } = React.useMemo(() => {
		const columns = ["student"];
		const columnWidths = {
			student: "16rem",
		};

		if (gradeBookQueryStatus === "loading") {
			columns.push("loading");
		} else if (gradeBookQueryStatus === "no-results") {
			columns.push("noResults");
		} else if (gradeBookData) {
			for (let i = 0; i < gradeBookData.courseAssessmentTimingConnection.edges.length; i++) {
				const timing = gradeBookData.courseAssessmentTimingConnection.edges[i].node;
				columns.push(timing.id);
				columnWidths[timing.id] = "minmax(10rem, 1fr)";
			}
		}
		return { columns, columnWidths };
	}, [gradeBookData, gradeBookQueryStatus]);

	const studentSubmissionMarks = React.useMemo(() => {
		const studentSubmissionMarks: Record<
			Student["id"],
			Record<
				CourseAssessment["id"],
				{
					submission:
						| CourseSectionGradeBookQuery["courseAssessmentTimingConnection"]["edges"][0]["node"]["courseAssessment"]["courseAssessmentSubmissions"][0]
						| null;
					mark:
						| CourseSectionGradeBookQuery["courseAssessmentTimingConnection"]["edges"][0]["node"]["courseAssessment"]["courseAssessmentMarks"][0]
						| null;
				}
			>
		> = {};

		if (!gradeBookData) {
			return studentSubmissionMarks;
		}

		for (let i = 0; i < gradeBookData.courseAssessmentTimingConnection.edges.length; i++) {
			const assessment = gradeBookData.courseAssessmentTimingConnection.edges[i].node.courseAssessment;
			for (let j = 0; j < assessment.courseAssessmentSubmissions.length; j++) {
				const submission = assessment.courseAssessmentSubmissions[j];
				if (!studentSubmissionMarks[submission.studentID]) {
					studentSubmissionMarks[submission.studentID] = {};
				}
				if (!studentSubmissionMarks[submission.studentID][assessment.id]) {
					studentSubmissionMarks[submission.studentID][assessment.id] = { submission: null, mark: null };
				}
				studentSubmissionMarks[submission.studentID][assessment.id].submission = submission;
			}
			for (let j = 0; j < assessment.courseAssessmentMarks.length; j++) {
				const mark = assessment.courseAssessmentMarks[j];
				if (!studentSubmissionMarks[mark.studentID]) {
					studentSubmissionMarks[mark.studentID] = {};
				}
				if (!studentSubmissionMarks[mark.studentID][assessment.id]) {
					studentSubmissionMarks[mark.studentID][assessment.id] = { submission: null, mark: null };
				}
				studentSubmissionMarks[mark.studentID][assessment.id].mark = mark;
			}
		}

		return studentSubmissionMarks;
	}, [gradeBookData]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem">
			<SearchField formState={searchFormState} name="search" />

			<Table columns={columns} columnWidths={columnWidths} className={styles["table"]}>
				<Table.Header>
					<Table.Row>
						<Table.Heading
							column="student"
							className={styles["table__heading"] + " " + styles["table__heading--student"]}
						></Table.Heading>
						{gradeBookData?.courseAssessmentTimingConnection.edges.map(({ node: timing }) => (
							<Table.Heading
								key={timing.id}
								column={timing.id}
								className={styles["table__heading"] + " " + styles["table__heading--course-assessment"]}
							>
								<CourseAssessmentButton courseAssessmentTiming={timing} courseSectionID={courseSectionID} />
							</Table.Heading>
						))}
						<Conditional>
							<If condition={gradeBookQueryStatus === "loading"}>
								<Table.Heading column="loading" className={styles["table__heading"]}>
									Assessment
								</Table.Heading>
							</If>
							<If condition={gradeBookQueryStatus === "no-results"}>
								<Table.Heading column="noResults" className={styles["table__heading"]}>
									Assessment
								</Table.Heading>
							</If>
						</Conditional>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{filteredStudentEdges.map(({ node: student }) => {
						const assessmentSubmissionMarks = studentSubmissionMarks[student.id];

						return (
							<Table.Row key={student.id}>
								<Table.Cell
									column="student"
									oneLine
									className={styles["table__cell"] + " " + styles["table__cell--student"]}
								>
									<StudentButton student={student} courseSectionID={courseSectionID} />
								</Table.Cell>
								{gradeBookData?.courseAssessmentTimingConnection.edges.map(({ node: timing }) => {
									const submissionMark = assessmentSubmissionMarks?.[timing.courseAssessment.id];
									const submission = submissionMark?.submission;
									const mark = submissionMark?.mark;

									return (
										<Table.Cell key={timing.id} column={timing.id} oneLine className={styles["table__cell"]}>
											<Row justify="spaced-start" align="center">
												<strong>{mark && mark.isExcused ? "-" : mark?.points ?? "-"}</strong>
												<SubmissionStatusBadge courseAssessmentSubmission={submission} courseAssessmentMark={mark} />
											</Row>
										</Table.Cell>
									);
								})}
								<Conditional>
									<If condition={gradeBookQueryStatus === "loading"}>
										<Table.Cell column="loading" className={styles["table__cell"]}>
											Loading...
										</Table.Cell>
									</If>
									<If condition={gradeBookQueryStatus === "no-results"}>
										<Table.Cell column="noResults" className={styles["table__cell"]}>
											No assessments found
										</Table.Cell>
									</If>
								</Conditional>
							</Table.Row>
						);
					})}
				</Table.Body>
			</Table>
		</Column>
	);
}

type CourseAssessmentButtonProps = {
	courseAssessmentTiming: CourseSectionGradeBookQuery["courseAssessmentTimingConnection"]["edges"][0]["node"];
	courseSectionID: CourseSection["id"];
};

function CourseAssessmentButton({ courseAssessmentTiming, courseSectionID }: CourseAssessmentButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} className={styles["course-assessment-button"]}>
				<Column justify="spaced-start" verticalSpacing="0.25rem">
					<span>{CourseAssessmentTimingFormat.Fields.dueOn(courseAssessmentTiming.dueOn)}</span>
					<TruncatedText className={styles["course-assessment-name"]}>
						{CourseAssessmentFormat.name(courseAssessmentTiming.courseAssessment)}
					</TruncatedText>
					<span>
						out of {CourseAssessmentFormat.Fields.totalPoints(courseAssessmentTiming.courseAssessment.totalPoints)}
					</span>
				</Column>
			</Button>

			<Modal.If condition={isModalOpen}>
				<ControlledCourseAssessmentModal
					courseAssessmentID={courseAssessmentTiming.courseAssessment.id}
					courseSectionID={courseSectionID}
					withBackButton
					backButtonText="Back to Grade Book"
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}

type StudentButtonProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	courseSectionID: CourseSection["id"];
};

function StudentButton({ student, courseSectionID }: StudentButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button onClick={openModal} className={styles["student-button"]}>
				<Row justify="spaced-start" align="center">
					<StudentIcon student={student} size={24} />
					<TruncatedText className={styles["student-name"]}>{StudentFormat.name(student)}</TruncatedText>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<StudentCourseSectionGradeBookModal
					student={student}
					courseSectionID={courseSectionID}
					withBackButton
					backButtonText="Back to Grade Book"
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
